"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getConsentState, setConsent } from "@/app/lib/consent";
import { loadGtm, loadMetaPixel } from "@/app/lib/tracking";

// Ad tags (Tag Manager, Meta Pixel) are opt-in: they only load after "OK"
const loadAdTags = () => {
  loadGtm();
  loadMetaPixel();
};

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  // Server-rendered so first-time visitors see it at first paint (it is the
  // mobile LCP element). For visitors who already chose, the root layout's
  // inline script hides it before paint and this effect then removes it.
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const consent = getConsentState();
    if (consent !== null) setShowBanner(false);
    if (consent === "accepted") loadAdTags();
  }, []);

  const handleAccept = () => {
    setConsent(true);
    loadAdTags();
    setShowBanner(false);
  };

  const handleDecline = () => {
    setConsent(false);
    setShowBanner(false);
    // Reload to stop tracking
    window.location.reload();
  };

  if (!showBanner) return null;

  return (
    // Blocking overlay: the page stays unusable until the visitor picks one
    <div
      data-cookie-banner
      className="fixed inset-0 z-[100] flex items-center justify-center bg-signal-ink/40 px-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        className="glass-strong bg-[var(--lg-solid-strong)] w-full max-w-md rounded-glass p-6"
      >
        <p
          id="cookie-consent-title"
          className="text-[18px] font-semibold text-signal-ink"
        >
          {t("common.cookieConsent.title", "Cookies for a better experience")}
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-signal-muted">
          {t("common.cookieConsent.message")}{" "}
          <a
            href="https://achtfach.ch/datenschutz?lang=en#cookies"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-signal-ink"
          >
            {t("common.cookieConsent.more", "Privacy policy")}
          </a>
        </p>
        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            onClick={handleDecline}
            className="btn-glass-ghost px-5 py-2.5 text-[15px]"
          >
            {t("common.cookieConsent.decline", "Decline")}
          </button>
          <button
            autoFocus
            onClick={handleAccept}
            className="btn-glass-primary px-5 py-2.5 text-[15px]"
          >
            {t("common.cookieConsent.accept", "Accept cookies")}
          </button>
        </div>
      </div>
    </div>
  );
}
